// frontend/src/lib/dramaDownload/taskCollection.ts
import type { Shot, ShotVersion, SubtitleEraseTask } from '../../types';
import type { DownloadTask } from './types';

// 剧集详情页把擦除版选为主版本/星标时，持久化的 id 形如 `erased:<versionId>`
export const ERASED_ID_PREFIX = 'erased:';

const ILLEGAL = /[/\\:*?"<>|]/g;

export function sanitizeName(name: string): string {
  const replaced = name.replace(ILLEGAL, '_');
  const runes = Array.from(replaced);
  return runes.length > 50 ? runes.slice(0, 50).join('') : replaced;
}

// 前端类型标注 secondaryVersionIds 为 string，但 spec §5 要求兼容后端可能直接返回数组，
// 故防御式接受 string | string[]（成本极低，避免运行时拿到数组时星标后缀丢失）。
export function parseSecondaryIds(raw?: string | string[] | null): string[] {
  if (Array.isArray(raw)) return raw.filter((x): x is string => typeof x === 'string');
  if (!raw || raw === 'null') return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

// 病态范围（如 '1-99999999'）会同步展开成巨大 Set 冻结页面。
// 真实剧集数远低于此上限；下游 applyRange 会与真实剧集求交，裁剪原始展开是安全的。
export const MAX_EPISODE_RANGE = 10000;

export function parseEpisodeRange(input: string): Set<number> | null {
  const s = input.trim();
  if (s === '') return null; // null = 全部
  const result = new Set<number>();
  for (const partRaw of s.split(',')) {
    const part = partRaw.trim();
    if (part.includes('-')) {
      const [loS, hiS] = part.split('-');
      const lo = parseInt((loS ?? '').trim(), 10);
      const hi = parseInt((hiS ?? '').trim(), 10);
      if (Number.isNaN(lo) || Number.isNaN(hi)) continue;
      for (let i = lo; i <= hi && result.size < MAX_EPISODE_RANGE; i++) result.add(i);
    } else {
      const n = parseInt(part, 10);
      if (!Number.isNaN(n)) result.add(n);
    }
  }
  return result;
}

export function pad3(n: number): string {
  return String(n).padStart(3, '0');
}

// 进度指标格式化（spec §3⑤：总进度条含已下大小/速度/剩余时间；§3⑥完成汇总含总大小/耗时）
export function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${i === 0 ? Math.round(v) : v.toFixed(1)} ${units[i]}`;
}

export function formatDuration(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) return '0s';
  const s = Math.round(sec);
  const m = Math.floor(s / 60);
  return m > 0 ? `${m}m${s % 60}s` : `${s}s`;
}

export function formatRate(bytesPerSec: number): string {
  return Number.isFinite(bytesPerSec) && bytesPerSec > 0 ? `${formatBytes(bytesPerSec)}/s` : '—';
}

function resolveTag(
  id: string,
  selectedVersionId: string | undefined,
  starSet: Set<string>,
): { tag: DownloadTask['tag']; suffix: string } {
  if (id === selectedVersionId) return { tag: 'selected', suffix: '_selected' };
  if (starSet.has(id)) return { tag: 'star', suffix: '_star' };
  return { tag: '', suffix: '' };
}

export function collectShotTasks(
  shot: Shot,
  versions: ShotVersion[],
  dirName: string,
  eraseTasks: Record<string, SubtitleEraseTask> = {},
): DownloadTask[] {
  const tasks: DownloadTask[] = [];
  const starSet = new Set(parseSecondaryIds(shot.secondaryVersionIds));

  for (const v of versions) {
    if (v.videoURL) {
      const { tag, suffix } = resolveTag(v.id, shot.selectedVersionId, starSet);
      tasks.push({
        shotId: shot.id,
        versionId: v.id,
        videoURL: v.videoURL,
        localPath: `${dirName}/shot_${pad3(shot.index)}_v${v.version}${suffix}.mp4`,
        tag,
      });
    }
    // 字幕擦除版：详情页命名约定为 v{n}.1；即使原版本 videoURL 丢失也照常下载擦除版
    const erase = eraseTasks[v.id];
    if (erase?.status === 'completed' && erase.resultVideoUrl) {
      const erasedId = `${ERASED_ID_PREFIX}${v.id}`;
      const { tag, suffix } = resolveTag(erasedId, shot.selectedVersionId, starSet);
      tasks.push({
        shotId: shot.id,
        versionId: erasedId,
        videoURL: erase.resultVideoUrl,
        localPath: `${dirName}/shot_${pad3(shot.index)}_v${v.version}.1_erased${suffix}.mp4`,
        tag,
      });
    }
  }

  if (tasks.length === 0 && shot.videoURL) {
    tasks.push({
      shotId: shot.id,
      versionId: shot.id,
      videoURL: shot.videoURL,
      localPath: `${dirName}/shot_${pad3(shot.index)}_v${shot.version}.mp4`,
      tag: 'selected',
    });
  }
  return tasks;
}

export function collectEpisodeTasks(
  episodeName: string,
  shots: Shot[],
  versionsByShot: Record<string, ShotVersion[]>,
  eraseTasks: Record<string, SubtitleEraseTask> = {},
): DownloadTask[] {
  const dirName = sanitizeName(episodeName);
  const tasks: DownloadTask[] = [];
  for (const shot of shots) {
    tasks.push(...collectShotTasks(shot, versionsByShot[shot.id] ?? [], dirName, eraseTasks));
  }
  return tasks;
}

export interface AIClipDownloadSummary {
  id: string;
  attemptNo: number;
  status: string;
}

export type AIClipArtifact = 'video' | 'premiere';

export interface AIClipHistoryPageLike<T extends AIClipDownloadSummary> {
  items: T[];
  nextCursor: string | null;
}

export async function listAllAIClipTasks<T extends AIClipDownloadSummary>(
  episodeId: string,
  listPage: (
    episodeId: string,
    query: { limit: number; cursor?: string },
  ) => Promise<AIClipHistoryPageLike<T>>,
): Promise<T[]> {
  const tasks: T[] = [];
  let cursor: string | undefined;
  do {
    const page = await listPage(episodeId, { limit: 50, ...(cursor ? { cursor } : {}) });
    tasks.push(...page.items);
    cursor = page.nextCursor ?? undefined;
  } while (cursor);
  return tasks;
}

export function collectAIClipTasks(
  episodeId: string,
  episodeName: string,
  clipTasks: AIClipDownloadSummary[],
  resolveArtifactURL: (episodeId: string, taskId: string, artifact: AIClipArtifact) => Promise<string>,
): DownloadTask[] {
  const dirName = sanitizeName(episodeName);
  const tasks: DownloadTask[] = [];
  for (const clip of clipTasks) {
    if (clip.status !== 'succeeded') continue;
    const artifacts: Array<{ kind: AIClipArtifact; fileName: string }> = [
      { kind: 'video', fileName: `ai_clip_v${clip.attemptNo}.mp4` },
      { kind: 'premiere', fileName: `premiere_v${clip.attemptNo}.xml` },
    ];
    for (const artifact of artifacts) {
      tasks.push({
        shotId: `ai-clip:${clip.id}`,
        versionId: artifact.kind,
        videoURL: '',
        sourceKey: `ai-clip:${clip.id}:${artifact.kind}`,
        resolveURL: () => resolveArtifactURL(episodeId, clip.id, artifact.kind),
        localPath: `${dirName}/${artifact.fileName}`,
        tag: '',
      });
    }
  }
  return tasks;
}
